import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"

// Connects to data-controller="settings--breadcrumb"
export default class extends Controller {
    static targets = ["breadcrumbList"]

    static values = {
        deviceTypeId: String,
        manufacturerId: String,
        taskId: String,
        paperTypeId: String
    }

    connect() {
        this.buildBreadcrumbs()
    }

    async buildBreadcrumbs() {
        const path = window.location.pathname
        const breadcrumbs = await this.parsePath(path)
        this.render(breadcrumbs)
    }

    async parsePath(path) {
        const breadcrumbs = []

        // Device Types hierarchy
        if (path.includes('/settings/device-types')) {
            breadcrumbs.push({ text: 'Device Types', url: '/settings/device-types' })

            if (this.hasDeviceTypeIdValue && this.deviceTypeIdValue) {
                const deviceTypeName = await this.fetchDeviceTypeName(this.deviceTypeIdValue)
                breadcrumbs.push({ text: deviceTypeName, url: `/settings/device-types/${this.deviceTypeIdValue}` })

                if (this.hasManufacturerIdValue && this.manufacturerIdValue) {
                    const manufacturerName = await this.fetchManufacturerName(this.manufacturerIdValue)
                    breadcrumbs.push({ text: manufacturerName, url: `/settings/device-types/${this.deviceTypeIdValue}` })
                    breadcrumbs.push({ text: 'Models', url: null })
                } else {
                    breadcrumbs.push({ text: 'Manufacturers', url: null })
                }
            }
        }

        // Device Testing Tasks hierarchy
        else if (path.includes('/settings/device-testing-tasks')) {
            breadcrumbs.push({ text: 'Device Testing Tasks', url: '/settings/device-testing-tasks' })

            if (this.hasTaskIdValue && this.taskIdValue) {
                const taskName = await this.fetchTestingTaskName(this.taskIdValue)
                breadcrumbs.push({ text: taskName, url: `/settings/device-testing-tasks/${this.taskIdValue}` })
                breadcrumbs.push({ text: 'Statuses', url: null })
            }
        }

        // Paper Types hierarchy
        else if (path.includes('/settings/paper-types')) {
            breadcrumbs.push({ text: 'Paper Types', url: '/settings/paper-types' })

            if (this.hasPaperTypeIdValue && this.paperTypeIdValue) {
                const paperTypeName = await this.fetchPaperTypeName(this.paperTypeIdValue)
                breadcrumbs.push({ text: paperTypeName, url: `/settings/paper-types/${this.paperTypeIdValue}` })
                breadcrumbs.push({ text: 'Print Classes', url: null })
            }
        }

        return breadcrumbs
    }

    async fetchDeviceTypeName(id) {
        try {
            const response = await fetch(`/api/device_types/device_type/describe?id=${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })
            if (response.ok) {
                const data = await response.json()
                return data.device_types[0]?.name || 'Unknown'
            }
        } catch (error) {
            console.error('Error fetching device type name:', error)
        }
        return 'Unknown'
    }

    async fetchManufacturerName(id) {
        try {
            const response = await fetch(`/api/device_types/device_manufacturer/describe?id=${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })
            if (response.ok) {
                const data = await response.json()
                return data.device_type_definitions[0]?.name || 'Unknown'
            }
        } catch (error) {
            console.error('Error fetching manufacturer name:', error)
        }
        return 'Unknown'
    }

    async fetchTestingTaskName(id) {
        try {
            const response = await fetch(`/api/testing_tasks/describe?id=${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })
            if (response.ok) {
                const result = await response.json()
                return result.data?.name || 'Unknown'
            }
        } catch (error) {
            console.error('Error fetching testing task name:', error)
        }
        return 'Unknown'
    }

    async fetchPaperTypeName(id) {
        try {
            const response = await fetch(`/api/print_mappings/paper_type/describe?id=${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })
            if (response.ok) {
                const data = await response.json()
                return data.paper_types[0]?.name || 'Unknown'
            }
        } catch (error) {
            console.error('Error fetching paper type name:', error)
        }
        return 'Unknown'
    }

    render(breadcrumbs) {
        if (!this.hasBreadcrumbListTarget) {
            return
        }

        let html = ''
        breadcrumbs.forEach((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1

            if (crumb.url && !isLast) {
                html += `<li class="breadcrumb-item"><a href="${crumb.url}">${crumb.text}</a></li>`
            } else {
                html += `<li class="breadcrumb-item ${isLast ? 'active' : ''}" ${isLast ? 'aria-current="page"' : ''}>${crumb.text}</li>`
            }
        })

        this.breadcrumbListTarget.innerHTML = html
    }
}
